import { flattenTree, type TreeItems } from '@/components/ui/sortable-tree';
import type { Page } from '@/types/page';
import type { ReorderOperation } from './page-utils';
import type { SortablePageItemData } from './sortable-page-item';

// Public navigation includes the first three levels of the page tree.
const PUBLIC_NAVIGATION_MAX_DEPTH = 3;

function findAncestors(pages: Page[], targetId: string, ancestors: Page[] = []): Page[] | null {
  for (const page of pages) {
    if (page.id === targetId) {
      return ancestors;
    }

    if (page.children?.length) {
      const result = findAncestors(page.children, targetId, [...ancestors, page]);
      if (result !== null) return result;
    }
  }

  return null;
}

function findPageById(
  pages: Page[],
  pageId: string,
  depth = 0,
): { page: Page; depth: number } | undefined {
  for (const page of pages) {
    if (page.id === pageId) {
      return { page, depth };
    }

    if (page.children?.length) {
      const foundChild = findPageById(page.children, pageId, depth + 1);
      if (foundChild) {
        return foundChild;
      }
    }
  }

  return undefined;
}

function countPublishedDescendants(page: Page, remainingDepth: number): number {
  if (remainingDepth <= 0) return 0;

  return (page.children || []).reduce((count, child) => {
    return (
      count + (child.isPublished ? 1 : 0) + countPublishedDescendants(child, remainingDepth - 1)
    );
  }, 0);
}

function countPublishedPagesInSubtree(page: Page, remainingDepth: number): number {
  if (remainingDepth <= 0) return 0;

  return (page.isPublished ? 1 : 0) + countPublishedDescendants(page, remainingDepth - 1);
}

function getUnpublishedAncestorTitle(items: TreeItems<SortablePageItemData>, pageId: string) {
  let current = flattenTree(items).find((item) => String(item.id) === pageId)?.parent;

  while (current) {
    if (!current.page.isPublished) {
      return current.page.navigationTitle || current.page.title;
    }

    current = current.parent;
  }

  return null;
}

export function getUnpublishWarningCount(allPages: Page[], page: Page) {
  const foundPage = findPageById(allPages, page.id);
  const pageDepth = foundPage?.depth ?? 0;
  const pageTree = foundPage?.page ?? page;
  const remainingDepth = PUBLIC_NAVIGATION_MAX_DEPTH - pageDepth - 1;

  return countPublishedDescendants(pageTree, remainingDepth);
}

export function getPublishParentWarning(allPages: Page[], pageId: string): string | null {
  const foundPage = findPageById(allPages, pageId);
  if (foundPage && foundPage.depth >= PUBLIC_NAVIGATION_MAX_DEPTH) {
    return null;
  }

  const ancestors = findAncestors(allPages, pageId);
  if (!ancestors) return null;

  for (let i = ancestors.length - 1; i >= 0; i--) {
    if (!ancestors[i].isPublished) {
      return ancestors[i].navigationTitle || ancestors[i].title;
    }
  }

  return null;
}

export interface GetReorderVisibilityWarningParams {
  previousItems: TreeItems<SortablePageItemData>;
  newItems: TreeItems<SortablePageItemData>;
  draggedPage: Page;
  draggedPageId: string;
}

export function getReorderVisibilityWarning({
  previousItems,
  newItems,
  draggedPage,
  draggedPageId,
}: GetReorderVisibilityWarningParams): boolean {
  const draggedItem = flattenTree(newItems).find((item) => String(item.id) === draggedPageId);
  const newDepth = draggedItem?.depth ?? 0;
  const affectedPublishedPageCount = countPublishedPagesInSubtree(
    draggedPage,
    PUBLIC_NAVIGATION_MAX_DEPTH - newDepth,
  );
  const previousUnpublishedAncestorTitle = getUnpublishedAncestorTitle(
    previousItems,
    draggedPageId,
  );
  const unpublishedAncestorTitle = getUnpublishedAncestorTitle(newItems, draggedPageId);

  return (
    affectedPublishedPageCount > 0 &&
    !!unpublishedAncestorTitle &&
    !previousUnpublishedAncestorTitle
  );
}

export interface PendingReorderVisibilityWarning {
  newItems: TreeItems<SortablePageItemData>;
  operation: ReorderOperation;
}
